import { Chip, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useCompanies } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'

/**
 * Which of the user's businesses the screen is showing.
 *
 * <p>Only appears for somebody who may work in more than one — an owner of
 * two, an accountant keeping both sets of books, or whoever administers the
 * platform and answers for all of them. Everyone else has one company and
 * never has to think about it.</p>
 *
 * <p>Switching throws away everything cached from the previous company. Nothing
 * on screen survives the switch, because none of it belongs to the business now
 * being looked at.</p>
 */
export function CompanySwitcher() {
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const activeCompanyId = useAuthStore((state) => state.activeCompanyId)
  const setActiveCompany = useAuthStore((state) => state.setActiveCompany)
  const can = useAuthStore((state) => state.can)
  const { data: companies } = useCompanies()

  // The companies this person was granted — or every one of them where they
  // administer the platform, which is what that permission means everywhere
  // else it is asked about. The server agrees: it answers a request for any
  // company when the caller holds it.
  const everywhere = can('platform.admin')
  const mine = everywhere
    ? (companies ?? [])
    : (companies ?? []).filter((company) => (user?.companyIds ?? []).includes(company.id))
  const active = activeCompanyId ?? user?.companyId ?? ''

  // One business: nothing to switch between, but the name still belongs on
  // screen. Every figure, list and setting under it is that business's, and a
  // group that runs two of them will be reading the same screens for both.
  if (mine.length < 2) {
    const only = mine[0] ?? companies?.find((company) => company.id === active)
    if (!only) {
      return null
    }
    return (
      <Tooltip title="The business you are working in">
        <Typography
          variant="body2"
          sx={{
            mr: 1.5,
            maxWidth: 220,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {only.name}
        </Typography>
      </Tooltip>
    )
  }

  return (
    <Tooltip title="The business you are working in">
      <TextField
        select
        size="small"
        value={active}
        onChange={(event) => {
          setActiveCompany(event.target.value)
          // Everything loaded belongs to the company being left, so it is all
          // thrown away. resetQueries rather than clear: clear empties the
          // cache but leaves the screen you are already on showing the figures
          // it fetched before the switch, because nothing asks it to fetch
          // again. This drops the data and refetches what is on screen.
          void queryClient.resetQueries()
        }}
        sx={{ minWidth: 190, mr: 1 }}
        inputProps={{ 'aria-label': 'Working in' }}
      >
        {mine.map((company) => (
          <MenuItem key={company.id} value={company.id}>
            {company.name}
            {company.id === user?.companyId ? (
              <Chip size="small" variant="outlined" label="Home" sx={{ ml: 1 }} />
            ) : null}
          </MenuItem>
        ))}
      </TextField>
    </Tooltip>
  )
}
